import { editor, Uri, languages } from 'monaco-editor';
import EditorWorkerURL from 'monaco-editor/esm/vs/editor/editor.worker?worker&url';
import CssWorkerURL from 'monaco-editor/esm/vs/language/css/css.worker?worker&url';
import YamlWorkerURL from 'monaco-yaml/yaml.worker?worker&url';
import { format } from 'prettier';
import parseCss from 'prettier/plugins/postcss';
// FIXME prettier yaml formatting
import parseYaml from 'prettier/plugins/yaml';

import { setDiagnosticsOptions } from 'monaco-yaml';
import type { JSONSchema4 } from 'json-schema';
import { snippets } from './snippets';
import { darkTheme, lightTheme } from './monacoThemes';
import {
  CourseType,
  getSchemaForActivity,
  updateMonacoSchema
} from './schemaRegistry';

editor.defineTheme('uc-dark', darkTheme);
editor.defineTheme('uc-light', lightTheme);
// editor.setTheme('one-dark');

const createWorker = (url: string, label: string) =>
  new Worker(new URL(url, import.meta.url), {
    type: 'module',
    name: label
  });

window.MonacoEnvironment = {
  getWorker(_, label) {
    switch (label) {
      case 'editorWorkerService':
        return createWorker(EditorWorkerURL, label);
      case 'css':
      case 'scss':
        return createWorker(CssWorkerURL, label);
      case 'yaml':
        return createWorker(YamlWorkerURL, label);
      default:
        throw new Error(`Unknown label ${label}`);
    }
  }
};

// Initialize with default schema (Full Course)
setDiagnosticsOptions({
  enableSchemaRequest: true,
  hover: true,
  completion: true,
  validate: true,
  format: false,
  schemas: [updateMonacoSchema(CourseType.FullCourse)]
});

export const updateEditorSchema = (activityType: CourseType) => {
  setDiagnosticsOptions({
    enableSchemaRequest: true,
    hover: true,
    completion: true,
    validate: true,
    format: false,
    schemas: [updateMonacoSchema(activityType)]
  });
};

// based on https://microsoft.github.io/monaco-editor/playground.html?source=v0.41.0#example-extending-language-services-completion-provider-example
languages.registerCompletionItemProvider('yaml', {
  provideCompletionItems: function (model, position) {
    // Match the last line that has at least 6 spaces a dash.
    const precedingText = model.getValueInRange({
      startLineNumber: position.lineNumber - 1,
      endLineNumber: position.lineNumber,
      startColumn: 1,
      endColumn: position.column
    });

    const match = precedingText.match(/\n {6}\s*-(\s*)$/);
    if (!match) return { suggestions: [] };

    // make sure we line up the snippet result indentation
    const spacesAfterDash = match[1].length;
    const startingSpace =
      spacesAfterDash === 0 ? ' ' : spacesAfterDash === 1 ? '' : `\n  `;

    const word = model.getWordUntilPosition(position);

    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn
    };

    return {
      suggestions: snippets.map((sn): languages.CompletionItem => {
        const { label, snippet } = sn;
        const string = snippet();

        return {
          label,
          range,
          sortText: 'aaa' + label,
          kind: languages.CompletionItemKind.Function,
          insertTextRules:
            languages.CompletionItemInsertTextRule.InsertAsSnippet,
          insertText: startingSpace + string.split(/\n {6}/g).join('\n  ')
        };
      })
    };
  }
});

languages.registerDocumentFormattingEditProvider(
  { language: 'yaml', exclusive: true },
  {
    provideDocumentFormattingEdits: async function (model) {
      const v = model.getValue();
      const text = await format(v, { parser: 'yaml', plugins: [parseYaml] });
      console.log('prettier');

      return [{ range: model.getFullModelRange(), text }];
    }
  }
);

// languages.css.cssDefaults.setModeConfiguration({
//   ...languages.css.cssDefaults.modeConfiguration,
//   documentFormattingEdits: false,
//   documentRangeFormattingEdits: false
// });

languages.registerDocumentFormattingEditProvider(
  { language: 'css', exclusive: true },
  {
    provideDocumentFormattingEdits: async function (model) {
      const v = model.getValue();
      const text = await format(v, { parser: 'css', plugins: [parseCss] });
      console.log('prettier');

      return [{ range: model.getFullModelRange(), text }];
    }
  }
);

export const createYamlModel = (name: string, value: string) =>
  editor.createModel(value, 'yaml', Uri.parse(`a://b/${name}.yml`));

export const createCssModel = (name: string, value: string) =>
  editor.createModel(value, 'css', Uri.parse(`a://b/${name}.css`));
